/**
 * Room Types Page
 * Display all room types for browsing (without dates)
 */

import { t, formatCurrency } from '../i18n';
import { Header, LoadingSpinner, ErrorMessage, HotelCard, AppVersionFooter, BackToTopButton, initBackToTop } from '../components';
import { getRoomTypes, getRoomImages, getHotelInfo, type RoomTypeInfo } from '../api';
import { getHotelCode, getTenantId } from '../config';
import { navigateTo } from '../store';
import { translateRoomType, translateRoomDescription, translateAmenity } from '../i18n';
import { closeWindow, isInLineApp } from '../liff';
import { openLightbox } from '../components/lightbox';
import { renderCarousel } from '../components/carousel';

// Local state for this page
let roomTypesData: RoomTypeInfo[] = [];
let isLoading = true;
let error: string | null = null;

/**
 * Render room types page
 */
export function renderRoomTypesPage(): string {
  if (isLoading) {
    return `
      ${Header(t('roomTypesTitle'), false, true)}
      <div class="page-content">
        ${LoadingSpinner(t('loading'))}
      </div>
    `;
  }

  if (error) {
    return `
      ${Header(t('roomTypesTitle'), false, true)}
      <div class="page-content">
        ${ErrorMessage(error)}
      </div>
    `;
  }

  if (roomTypesData.length === 0) {
    return `
      ${Header(t('roomTypesTitle'), false, true)}
      <div class="page-content">
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <p>${t('noRoomsAvailable')}</p>
        </div>
      </div>
    `;
  }

  // Room type cards
  const roomCards = roomTypesData.map((room, index) => RoomTypeCard(room, index)).join('');

  return `
    ${Header(t('roomTypesTitle'), false, true)}
    <div class="page-content room-types-page">
      <div id="hotel-card-container">${HotelCard()}</div>
      <div class="page-intro">
        <h2 class="intro-title">${t('ourRooms')}</h2>
      </div>
      <div class="rooms-list" id="rooms-list">
        ${roomCards}
      </div>
      ${AppVersionFooter()}
    </div>

    ${BackToTopButton('roomtypes')}
  `;
}

/**
 * Room Type Card Component (similar to RoomCard but without rate plan details)
 */
function RoomTypeCard(room: RoomTypeInfo, index: number): string {
  // Get translated names
  const roomTypeName = translateRoomType(room.roomTypeCode, room.name);
  const translatedDescription = translateRoomDescription(room.roomTypeCode, room.description);

  // Amenities (first 6)
  const amenitiesHtml = (room.amenities || [])
    .slice(0, 6)
    .map(amenity => {
      const translated = translateAmenity(amenity);
      return `<span class="amenity-tag">${translated}</span>`;
    })
    .join('');

  // Base rate display (++ indicates excluding taxes & service charge)
  const baseRateHtml =
    room.baseRate > 0
      ? `<div class="room-price">
        <span class="price-label">${t('startingFrom')}</span>
        <span class="price-amount">${formatCurrency(room.baseRate, room.currency)}<sup>++</sup></span>
        <span class="price-unit">${t('perNight')}</span>
        <div class="price-note">${t('excludingTaxes')}</div>
      </div>`
      : '';

  return `
    <div class="card room-card room-type-card" data-index="${index}">
      <div class="room-images" data-room-type="${room.roomTypeCode}">
        <div class="room-image-placeholder">
          <div class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
      
      <div class="room-info">
        <h3 class="room-name">${roomTypeName}</h3>
        ${room.bedType ? `<div class="room-bed-type" style="font-size:12px; color:#555; font-weight:600; margin-top:2px; margin-bottom:6px;">🛏️ ${room.bedType}${room.bedTypeDesc ? ` (${room.bedTypeDesc})` : ''}</div>` : ''}
        <p class="room-description">${translatedDescription}</p>
        
        <div class="room-amenities">
          ${amenitiesHtml}
        </div>
        
        ${baseRateHtml}
        
        <button class="btn btn-primary btn-check-availability" onclick="window.goToBooking(${index})">
          🔍 ${t('checkAvailability')}
        </button>
      </div>
    </div>
  `;
}

/**
 * Initialize room types page
 */
export async function initRoomTypesPage(): Promise<void> {
  console.log('[RoomTypes] Initializing...');

  // Load hotel info (logo, name, rating, address) - language-aware (resolves runtime hotelCode!)
  await loadHotelInfo();

  // Load room types data
  await loadRoomTypes();

  // Back button handler - close LIFF
  window.handleBack = () => {
    // Close the LIFF window
    if (isInLineApp()) {
      closeWindow();
    } else {
      // Fallback: go back or navigate to search
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigateTo('search');
      }
    }
  };

  // Go to booking handler
  window.goToBooking = (_index: number) => {
    // Navigate to search page to start booking flow
    navigateTo('search');
  };

  // Setup scroll handlers
  setupScrollHandlers();

  // Load image carousels
  await loadRoomCarousels();
}

/**
 * Load hotel info from API and update HotelCard (language-aware)
 */
async function loadHotelInfo(): Promise<void> {
  const container = document.getElementById('hotel-card-container');
  if (!container) return;

  try {
    const hotelInfo = await getHotelInfo();
    if (hotelInfo) {
      container.innerHTML = HotelCard({
        name: hotelInfo.hotelName,
        address: hotelInfo.hotelAddress,
        rating: hotelInfo.hotelRating,
        image: hotelInfo.hotelImage,
      });
    }
  } catch (err) {
    console.error('[RoomTypes] Failed to load hotel info:', err);
  }
}

/**
 * Load room types from API
 */
async function loadRoomTypes(): Promise<void> {
  isLoading = true;
  error = null;

  try {
    const hotelCode = getHotelCode();
    const result = await getRoomTypes(hotelCode);

    if (result && result.roomTypes) {
      roomTypesData = result.roomTypes;
    } else {
      error = t('errorGeneric');
    }
  } catch (err) {
    console.error('[RoomTypes] Load error:', err);
    error = t('errorNetwork');
  } finally {
    isLoading = false;
    // Re-render the page
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = renderRoomTypesPage();
      // Re-setup after render
      setupScrollHandlers();
      loadRoomCarousels();
      loadHotelInfo();
    }
  }
}

function setupScrollHandlers(): void {
  initBackToTop('roomtypes');
}

/**
 * Load and render carousels for each room card
 */
async function loadRoomCarousels(): Promise<void> {
  try {
    const tenantId = getTenantId();
    const hotelCode = getHotelCode();
    const containerEls = Array.from(document.querySelectorAll('.room-images')) as HTMLElement[];

    if (containerEls.length === 0) return;

    for (const el of containerEls) {
      const roomType = el.getAttribute('data-room-type');
      if (!roomType) continue;

        try {
          const images = await getRoomImages(tenantId, hotelCode, roomType);
          if (images && images.length > 0) {
            renderCarousel(el, images.slice(0, 15));
          } else {
          // Show default placeholder
          el.innerHTML = `
            <div class="room-image-placeholder">
              <span class="placeholder-icon">🏨</span>
            </div>
          `;
        }
      } catch (err) {
        console.warn('[RoomTypes] Failed to fetch images for', roomType, err);
      }
    }
  } catch (err) {
    console.error('[RoomTypes] loadRoomCarousels error', err);
  }
}



// Extend window for global handlers
declare global {
  interface Window {
    goToBooking: (index: number) => void;
  }
}
